import { observer } from 'mobx-react';
import { FC, Fragment, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Button, Divider, Flex, Icon, Text } from '@servicetitan/anvil2';
import IconClose from '@servicetitan/anvil2/assets/icons/material/round/close.svg';
import IconDocumentOther from '@servicetitan/anvil2/assets/icons/st/document_other.svg';
import IconEstimate from '@servicetitan/anvil2/assets/icons/st/estimate.svg';
import IconInvoice from '@servicetitan/anvil2/assets/icons/st/invoice.svg';
import { provide, useDependencies } from '@servicetitan/react-ioc';

import { OemPartSelect } from './oem-part-select';
import { ReplacementCard } from './replacement-card';
import { ViewEstimate } from './view-estimate';
import { ViewInvoice } from './view-invoice';
import { ViewNotes } from './view-notes';
import { InstalledEquipmentApiModel } from '../../common/api/equipments.api';
import { BottomDrawer } from '../../common/components/bottom-drawer/bottom-drawer';
import { Breadcrumbs } from '../../common/components/breadcrumbs/breadcrumbs';
import { ChatHeaderNew } from '../../common/components/chat-layout/chat-header-new';
import { ChatLayout } from '../../common/components/chat-layout/chat-layout';
import { Loader } from '../../common/components/loader/loader';
import { MessageBubble } from '../../common/components/message-bubble/message-bubble';
import { MessageRow } from '../../common/components/message-row/message-row';
import { EstimateSelect } from '../../common/estimate-select/compntents/estimate-select';
import { EstimateViewModel } from '../../common/estimate-select/models/estimate-view.model';
import { EstimateSelectStore } from '../../common/estimate-select/stores/estimate-select.store';
import { MessageType } from '../../common/models/message-type.model';
import { ChatStore, IMessage } from '../../common/stores/chat.store';
import { MainStore } from '../../common/stores/main.store';
import { EquipmentManualSelect } from '../../equipment-picker/components/equipment-manual-select';
import { SearchEquipment } from '../../equipment-picker/components/search-equipment';
import { ManualEquipmentData } from '../../equipment-picker/store/equipment-manual-select.store';
import { EquipmentPickerStore } from '../../equipment-picker/store/equipment-picker.store';
import { ReplacementPartViewModelWithMatch } from '../models/replacement-part-view-model';
import { ReplacementPartFinderStore } from '../stores/replacement-part-finder.store';
import { ReplacementPartsActionsStore } from '../stores/replacementpart-actions.store';
import * as Styles from '../styles/replacement-parts.module.less';

export const ReplacementPartFinder: FC = provide({
    singletons: [ReplacementPartFinderStore, ReplacementPartsActionsStore, EstimateSelectStore],
})(
    observer(() => {
        const [
            store,
            chatStore,
            equipmentStore,
            mainStore,
            replacementPartsActionsStore,
            estimateSelectStore,
        ] = useDependencies<
            [
                ReplacementPartFinderStore,
                ChatStore,
                EquipmentPickerStore,
                MainStore,
                ReplacementPartsActionsStore,
                EstimateSelectStore,
            ]
        >(
            ReplacementPartFinderStore,
            ChatStore,
            EquipmentPickerStore,
            MainStore,
            ReplacementPartsActionsStore,
            EstimateSelectStore
        );

        const [showActionsDrawer, setShowActionsDrawer] = useState(false);

        const [selectedReplacementPart, setSelectedReplacementPart] = useState<
            ReplacementPartViewModelWithMatch | undefined
        >();

        const history = useHistory();

        useEffect(() => {
            equipmentStore.loadEquipments();
            store.showSelectEquipment();

            return () => {
                equipmentStore.dispose();
            };
        }, [equipmentStore, history, mainStore.selectedAppointment?.id, store]);

        const handleNavigationButtonClick = () => {
            store.dispose();
            chatStore.resetMessaging();
            window.history.back();
        };

        const handleEquipmentClick = async (installedEquipment: InstalledEquipmentApiModel) => {
            // Placeholder for installedEquipment click handling logic
            const mappedEquipments = await equipmentStore.setSelectedEquipment(installedEquipment);

            if (mappedEquipments && mappedEquipments.length > 0) {
                store.loadParts(mappedEquipments, installedEquipment);
            } else {
                // todo: show error toast
            }
        };

        const handleManualEquipmentSubmit = async (data: ManualEquipmentData | undefined) => {
            // Placeholder for equipment click handling logic
            if (!data) {
                return;
            }
            const mappedEquipments = await equipmentStore.submitManualEquipment(data);
            if (mappedEquipments && mappedEquipments.length > 0) {
                store.loadParts(mappedEquipments);
            }
        };

        const handleReplacementPartAddButtonClick = (part: ReplacementPartViewModelWithMatch) => {
            setSelectedReplacementPart(part);
            setShowActionsDrawer(true);
        };

        const handleAddBottomDrawerClose = () => {
            setSelectedReplacementPart(undefined);
            setShowActionsDrawer(false);
        };

        const handleAddToInvoiceClick = async () => {
            if (selectedReplacementPart) {
                setShowActionsDrawer(false);
                await replacementPartsActionsStore.addPartToInvoice(selectedReplacementPart);
                setSelectedReplacementPart(undefined);
                chatStore.enqueueMessages([
                    {
                        showAvatar: false,
                        showBubble: false,
                        date: new Date(),
                        type: MessageType.CustomView,
                        isMine: false,
                        getView: () => <ViewInvoice />,
                    },
                ]);
            }
        };

        const handleAddToEstimateClick = async (estimates: EstimateViewModel[]) => {
            if (selectedReplacementPart) {
                setShowActionsDrawer(false);
                await replacementPartsActionsStore.addPartToEstimate(
                    selectedReplacementPart,
                    estimates
                );
                setSelectedReplacementPart(undefined);
                chatStore.enqueueMessages([
                    {
                        showAvatar: false,
                        showBubble: false,
                        date: new Date(),
                        type: MessageType.CustomView,
                        isMine: false,
                        getView: () => <ViewEstimate />,
                    },
                ]);
            }
        };

        const handleAddToEstimateMenuItemClick = () => {
            setShowActionsDrawer(false);
            if (estimateSelectStore.estimates.length > 1) {
                chatStore.enqueueMessages([
                    {
                        showAvatar: true,
                        showBubble: true,
                        data: selectedReplacementPart,
                        date: new Date(),
                        type: MessageType.CustomView,
                        isMine: false,
                        getView: () => <EstimateSelect onSubmit={handleAddToEstimateClick} />,
                    },
                ]);
            } else {
                handleAddToEstimateClick(estimateSelectStore.estimates);
            }
        };

        const handleCreateNoteClick = async () => {
            if (selectedReplacementPart) {
                setShowActionsDrawer(false);
                await replacementPartsActionsStore.addJobNote(selectedReplacementPart);
                setSelectedReplacementPart(undefined);
                chatStore.enqueueMessages([
                    {
                        showAvatar: false,
                        showBubble: false,
                        date: new Date(),
                        type: MessageType.CustomView,
                        isMine: false,
                        getView: () => <ViewNotes />,
                    },
                ]);
            }
        };

        const renderMessage = (message: IMessage, key: string) => {
            switch (message.type) {
                case MessageType.SearchEquipment:
                    return (
                        <MessageRow key={key} isMine={message.isMine} date={message.date}>
                            <SearchEquipment showWithDocsOnly onSubmit={handleEquipmentClick} />
                        </MessageRow>
                    );
                case MessageType.ManualEquipment:
                    return (
                        <MessageRow key={key} isMine={message.isMine} date={message.date}>
                            <EquipmentManualSelect
                                isLoading={equipmentStore.isFetchingInfo}
                                onSubmit={handleManualEquipmentSubmit}
                            />
                        </MessageRow>
                    );
                case MessageType.ReplacementOEMPartSelect:
                    return (
                        <MessageRow
                            key={key}
                            isMine={message.isMine}
                            date={message.date}
                            showAvatar={message.showAvatar}
                        >
                            <MessageBubble isMine={message.isMine}>
                                <OemPartSelect onSubmit={store.setSelectedOemPart} />
                            </MessageBubble>
                        </MessageRow>
                    );
                case MessageType.ReplacementsList:
                    return (
                        <MessageRow
                            key={key}
                            isMine={message.isMine}
                            date={message.date}
                            showAvatar={message.showAvatar}
                        >
                            <Flex direction="column" gap="2" className="w-100-i">
                                {(message.data as ReplacementPartViewModelWithMatch[]).map(
                                    (x, index) => {
                                        return (
                                            <Fragment key={`${x.id}-${JSON.stringify(x.match)}`}>
                                                <ReplacementCard
                                                    replacementPart={x}
                                                    isLoadingPricebookMatches={
                                                        store.isLoadingPricebookMatches
                                                    }
                                                    onSelect={handleReplacementPartAddButtonClick}
                                                />
                                                {index === 0 && message.id && (
                                                    <span id={message.id} />
                                                )}
                                            </Fragment>
                                        );
                                    }
                                )}
                            </Flex>
                        </MessageRow>
                    );

                case MessageType.Text:
                    return (
                        <MessageRow
                            key={key}
                            isMine={message.isMine}
                            showAvatar={message.showAvatar}
                            name={message.isMine ? 'Tenant Name' : 'TI'}
                            date={message.date}
                            data-cy={`message-row-${key}`}
                        >
                            <MessageBubble isMine={message.isMine}>
                                <Text>{message.text}</Text>
                            </MessageBubble>
                        </MessageRow>
                    );

                case MessageType.RichText:
                    return (
                        <MessageRow
                            key={key}
                            isMine={message.isMine}
                            showAvatar={message.showAvatar}
                            date={message.date}
                        >
                            <MessageBubble isMine={message.isMine}>
                                <Text>
                                    {/* eslint-disable react/no-danger,@typescript-eslint/naming-convention */}
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html: message.text ?? '',
                                        }}
                                    />
                                    {/* eslint-enable react/no-danger,@typescript-eslint/naming-convention */}
                                </Text>
                            </MessageBubble>
                        </MessageRow>
                    );

                case MessageType.CustomView:
                    return (
                        <MessageRow
                            key={key}
                            isMine={message.isMine}
                            showAvatar={message.showAvatar}
                            name={message.isMine ? 'Tenant Name' : 'TI'}
                            date={message.date}
                            data-cy={`message-row-${key}`}
                        >
                            <MessageBubble isMine={message.isMine} showBubble={message.showBubble}>
                                {message.getView?.()}
                            </MessageBubble>
                        </MessageRow>
                    );
                default:
                    return null;
            }
        };

        const showAddToEstimate =
            mainStore.hasPermissionToAddEstimateItem &&
            !!selectedReplacementPart?.match &&
            [1, 2].includes(selectedReplacementPart?.match?.type) &&
            (mainStore.jobDetails?.estimates ?? []).length > 0;

        const showAddToInvoice =
            mainStore.hasPermissionToAddInvoiceItem &&
            !!selectedReplacementPart?.match &&
            [1, 2].includes(selectedReplacementPart?.match?.type) &&
            !!mainStore.jobDetails?.invoiceId;

        const showAnnNote = mainStore.hasPermissionToAddNote;

        const hasEstimateOrInvoice = showAddToEstimate || showAddToInvoice;

        return (
            <ChatLayout
                subHeader={<Breadcrumbs />}
                header={
                    <ChatHeaderNew
                        onNavigationButtonClick={handleNavigationButtonClick}
                        icon="back"
                    />
                }
            >
                {chatStore.messages.map((message, index) => {
                    return renderMessage(message, index.toString());
                })}

                {(store.isLoading ||
                    store.isLoadingPricebookMatches ||
                    replacementPartsActionsStore.addingToEstimate ||
                    replacementPartsActionsStore.addingNoteToJob ||
                    replacementPartsActionsStore.addingToInvoice) && (
                    <MessageRow isMine={false}>
                        <Loader />
                    </MessageRow>
                )}

                <BottomDrawer
                    fullHeight
                    className={Styles.addActionsBottomDrawer}
                    open={showActionsDrawer}
                    onClose={handleAddBottomDrawerClose}
                >
                    <Flex direction="column">
                        <Flex
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            className={Styles.addActionsBottomDrawerTopBar}
                        >
                            <span style={{ width: 32 }} />
                            <Text justifySelf="center">
                                <b>Add</b>
                            </Text>
                            <Button
                                className={Styles.closeButton}
                                icon={IconClose}
                                onClick={handleAddBottomDrawerClose}
                                appearance="ghost"
                                size="small"
                            />
                        </Flex>

                        <Divider />

                        <Flex className="m-t-2" direction="column">
                            {showAddToInvoice && (
                                <Flex
                                    direction="row"
                                    alignItems="center"
                                    onClick={handleAddToInvoiceClick}
                                    className={Styles.actionButton}
                                >
                                    <Icon svg={IconInvoice} />
                                    <Text>
                                        <b>Add to Invoice</b>
                                    </Text>
                                </Flex>
                            )}

                            {showAddToEstimate && (
                                <Flex
                                    direction="row"
                                    alignItems="center"
                                    onClick={handleAddToEstimateMenuItemClick}
                                    className={Styles.actionButton}
                                >
                                    <Icon svg={IconEstimate} />
                                    <Text>
                                        <b>Add to Estimate</b>
                                    </Text>
                                </Flex>
                            )}

                            {showAnnNote && (
                                <Fragment>
                                    {hasEstimateOrInvoice && <Divider />}

                                    <Flex
                                        direction="row"
                                        alignItems="center"
                                        onClick={handleCreateNoteClick}
                                        className={Styles.actionButton}
                                    >
                                        <Icon svg={IconDocumentOther} />
                                        <Text>
                                            <b>Create Job Note</b>
                                        </Text>
                                    </Flex>
                                </Fragment>
                            )}
                        </Flex>
                    </Flex>
                </BottomDrawer>
            </ChatLayout>
        );
    })
);
